import React from "react";
import SubscriptionSection from "../components/SubscriptionSection";
import Toaster from "../components/Toaster";
import SvgDiscord from "../../../svg/SvgDiscord";
import {Link} from "react-router-dom";
import {
    Activity,
    ArrowRight,
    Backpack,
    Map as MapIcon,
    MessageSquare,
    Radio,
    Users,
} from "lucide-react";

export const DISCORD_INVITE = "https://discord.gg/WgyN4eKp8j";

export default function Index() {
    return <>
        <Toaster />
        <div className="w-full max-w-6xl mx-auto overflow-hidden flex-grow sm:pt-12 px-6 md:px-10">

            {/* Hero */}
            <section className="py-12 md:py-20">
                <div className="mx-auto text-center max-w-4xl">
                    <h1 className="text-5xl md:text-7xl font-bold mt-6 mb-8 text-white leading-[1.05] tracking-tight">
                        Manage your{" "}
                        <span className="bg-gradient-to-r from-primary to-sky-400 bg-clip-text text-transparent">
                            Rust Servers
                        </span>
                        <br/>
                        from anywhere
                    </h1>

                    <p className="text-lg md:text-xl text-neutral-400 max-w-2xl mx-auto mb-10">
                        Rust Server Tool helps you monitor, moderate, and grow your community
                        with precision, speed, and total control — all from your browser.
                    </p>

                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                        <Link to="/login"
                              className="inline-flex justify-center items-center gap-2 bg-gradient-to-r from-primary to-sky-400 hover:from-primary-hover hover:to-sky-300 text-white px-7 py-3.5 rounded-xl text-base font-semibold shadow-lg shadow-primary/20 transition-all">
                            Get Started for free
                            <ArrowRight className="size-4"/>
                        </Link>
                        <a href={DISCORD_INVITE} target="_blank" rel="noreferrer"
                           className="inline-flex justify-center items-center gap-3 border border-discord text-discord hover:bg-neutral-800 px-7 py-3.5 rounded-xl text-base font-semibold transition-colors">
                            <SvgDiscord className="text-discord"/>
                            <span>Join the Discord</span>
                        </a>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-12 md:py-20 scroll-mt-24">
                <div className="text-center mb-12 max-w-3xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
                        Everything you need,{" "}
                        <span className="bg-gradient-to-r from-primary to-sky-400 bg-clip-text text-transparent">
                            in one dashboard
                        </span>
                    </h2>
                    <p className="text-lg text-neutral-400">
                        From live maps to deep player insights — the tools that real admins
                        reach for, all on one screen.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 auto-rows-[minmax(0,1fr)]">
                    <FeatureCard
                        span="md:col-span-4"
                        gradient
                        icon={MapIcon}
                        title="Real-time interactive map"
                        description="See every player's position as they move, monuments and team colors. Spot raids forming and scout patterns — all updated live."
                    />
                    <FeatureCard
                        span="md:col-span-2"
                        icon={Radio}
                        title="24/7 connected"
                        description="We stay connected to your server around the clock — capturing chat, events, and stats even while you sleep."
                    />
                    <FeatureCard
                        span="md:col-span-2"
                        icon={Users}
                        title="Deep player profiles"
                        description="Steam history, VAC bans, aliases, IPs, play sessions, notes, and strikes — every metric one click away."
                    />
                    <FeatureCard
                        span="md:col-span-2"
                        icon={MessageSquare}
                        title="Live chat monitoring"
                        description="Read global, team, and clan chat in real time. Catch toxicity early and keep your community healthy."
                    />
                    <FeatureCard
                        span="md:col-span-2"
                        icon={Activity}
                        title="Performance graphs"
                        description="Track FPS, memory, player count, and entity load over time. Spot lag spikes before they cost you players."
                    />
                    <FeatureCard
                        span="md:col-span-6"
                        gradient
                        icon={Backpack}
                        title="Player inventory viewer"
                        description="Inspect any player's main, belt, and worn items in real time — with quantities, condition, and skins. Spot suspicious stockpiles and verify reports without ever logging in."
                    />
                </div>
            </section>

            {/* Pricing */}
            <hr id="pricing" className="border-none"/>
            <section className="py-12 md:py-20">
                <div className="text-center mb-12 max-w-3xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
                        Plans for every{" "}
                        <span className="bg-gradient-to-r from-primary to-sky-400 bg-clip-text text-transparent">
                            community size
                        </span>
                    </h2>
                    <p className="text-lg text-neutral-400">
                        Start free. Upgrade only when your server outgrows it.
                    </p>
                </div>
                <SubscriptionSection/>
            </section>
        </div>
    </>
}

function FeatureCard(props: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
    span: string;
    gradient?: boolean;
    comingSoon?: boolean;
}) {
    const Icon = props.icon

    const inner = (
        <div className={`relative h-full rounded-2xl p-6 md:p-7 flex flex-col bg-neutral-900 ${
            props.gradient ? "" : "border border-neutral-800"
        }`}>
            {props.comingSoon && (
                <div className="absolute top-4 right-4 bg-primary/15 text-primary text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border border-primary/30">
                    Coming soon
                </div>
            )}
            <div className={`inline-flex items-center justify-center size-11 rounded-xl mb-4 ${
                props.gradient
                    ? "bg-gradient-to-br from-primary to-sky-400 text-white"
                    : "bg-neutral-800 text-primary"
            }`}>
                <Icon className="size-5"/>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">{props.title}</h3>
            <p className="text-neutral-400 text-sm leading-relaxed">{props.description}</p>
        </div>
    )

    if (props.gradient) {
        return (
            <div className={`${props.span} rounded-2xl bg-gradient-to-br from-primary via-sky-400 to-primary p-[1.5px]`}>
                {inner}
            </div>
        )
    }

    return <div className={props.span}>{inner}</div>
}
